/**
 * Schema local-first — IndexedDB via Dexie (alternativa PWA ao SQLite).
 * Escolha UM dos dois caminhos do escopo: SQLite (RN/nativo) OU IndexedDB (PWA).
 *
 *   npm i dexie
 *
 * Fotos: guarde como Blob no próprio IndexedDB (tabela `foto`) e referencie por id.
 * Nada sai do aparelho sem ação explícita (export). Sem conta.
 */
import Dexie, { Table } from "dexie";
import { Alimento, Config, CONFIG_PADRAO, Crianca, Oferta } from "./types";
import type { ExperienciaMusical, MomentoMusical } from "./music/types";
import type { NotaSessao, Profissional } from "./elo/types";
import type { AtividadePsicomotora, MomentoPsicomotor } from "./body/types";
import type {
  Agendamento,
  Clinica,
  Disponibilidade,
  LocalClinica,
  MembroClinica,
} from "./agenda/types";

/**
 * Blob guardado localmente; `id` é o que vai em Alimento.fotos[] / Crianca.foto e
 * também nas mídias do Compasso (ExperienciaMusical.midias[] — foto ou clipe de
 * áudio). A tabela é genérica: o tipo real está em `blob.type`.
 */
export interface FotoLocal {
  id: string;
  blob: Blob;
  criado_em: string;
}

export class SeletividadeDB extends Dexie {
  crianca!: Table<Crianca, string>;
  alimento!: Table<Alimento, string>;
  oferta!: Table<Oferta, string>;
  foto!: Table<FotoLocal, string>;
  config!: Table<Config & { id: string }, string>;
  // Compasso (módulo de musicoterapia) — reusam `crianca` e `foto`.
  experiencia!: Table<ExperienciaMusical, string>;
  momento!: Table<MomentoMusical, string>;
  // Elo (módulo de integração) — reusam `crianca`, `oferta` e `foto`.
  profissional!: Table<Profissional, string>;
  nota!: Table<NotaSessao, string>;
  // Gingado (módulo de psicomotricidade) — reusam `crianca` e `foto`.
  atividade!: Table<AtividadePsicomotora, string>;
  momento_psm!: Table<MomentoPsicomotor, string>;
  // Agenda (camada de gestão de clínica) — reusa `crianca` como paciente.
  clinica!: Table<Clinica, string>;
  membro_clinica!: Table<MembroClinica, string>;
  agendamento!: Table<Agendamento, string>;
  disponibilidade!: Table<Disponibilidade, string>;
  local_clinica!: Table<LocalClinica, string>;

  constructor() {
    super("seletividade-alimentar");

    // Índices: só os campos consultados/filtrados. Objetos aninhados (sensorial)
    // ficam fora do índice — filtre em memória se precisar.
    this.version(1).stores({
      crianca: "id, criado_em",
      alimento: "id, status, arquivado, nome, criado_em",
      // [alimento_id+data_hora] acelera histórico por alimento; confirmado p/ pendências
      oferta: "id, alimento_id, crianca_id, data_hora, origem, confirmado, [alimento_id+data_hora]",
      foto: "id",
      config: "id",
    });

    // v2: módulo Compasso. Tabelas novas; as existentes seguem inalteradas.
    // [experiencia_id+data_hora] acelera o histórico por experiência.
    this.version(2).stores({
      experiencia: "id, status, arquivado, tipo, nome, criado_em",
      momento:
        "id, experiencia_id, crianca_id, data_hora, origem, confirmado, [experiencia_id+data_hora]",
    });

    // v3: módulo Elo (integração interdisciplinar). Tabelas novas.
    // [crianca_id+data_sessao] alimenta a timeline interdisciplinar.
    this.version(3).stores({
      profissional: "id, especialidade, nome, arquivado, criado_em",
      nota:
        "id, crianca_id, profissional_id, especialidade, alimento_id, data_sessao, [crianca_id+data_sessao]",
    });

    // v4: módulo Gingado (psicomotricidade). Tabelas novas; as existentes seguem
    // inalteradas. [atividade_id+data_hora] acelera o histórico por atividade.
    this.version(4).stores({
      atividade: "id, status, arquivado, tipo, nome, criado_em",
      momento_psm:
        "id, atividade_id, crianca_id, data_hora, origem, confirmado, [atividade_id+data_hora]",
    });

    // v5: Agenda / gestão de clínica (ADR-22). Tabelas NOVAS; as existentes seguem
    // inalteradas. Enquanto a agenda não for ativada (config.agenda_ativa=false),
    // ficam vazias e nada no app muda. [clinica_id+data] alimenta a grade do mês/dia.
    this.version(5).stores({
      clinica: "id, criado_em",
      membro_clinica: "id, clinica_id, papel, especialidade, arquivado",
      agendamento:
        "id, clinica_id, profissional_id, paciente_id, data, status, [clinica_id+data]",
      disponibilidade: "id, clinica_id, profissional_id, dia_semana",
      local_clinica: "id, clinica_id, arquivado",
    });
  }

  /** Garante a linha única de config. Chame no boot. */
  async garantirConfig(): Promise<Config> {
    const existente = await this.config.get("singleton");
    if (existente) return existente;
    const novo = { id: "singleton", ...CONFIG_PADRAO };
    await this.config.put(novo);
    return novo;
  }
}

export const db = new SeletividadeDB();

// ── Exemplos de acesso (os "repositórios" do app) ───────────────────────────

/** Registrar oferta (P0.2): incrementa contador implicitamente (é só inserir). */
export async function registrarOferta(o: Oferta): Promise<void> {
  await db.oferta.add(o);
}

/** Histórico de um alimento, mais recente primeiro. */
export function historico(alimentoId: string): Promise<Oferta[]> {
  return db.oferta
    .where("[alimento_id+data_hora]")
    .between([alimentoId, Dexie.minKey], [alimentoId, Dexie.maxKey])
    .reverse()
    .toArray();
}

/** Ofertas da criança pendentes de confirmação do cuidador. */
export function pendentesDeConfirmacao(): Promise<Oferta[]> {
  return db.oferta.where("confirmado").equals(0).toArray();
}

/** Lista rápida de alimentos seguros (P0.5). */
export function alimentosSeguros(): Promise<Alimento[]> {
  return db.alimento.where("status").equals("seguro").toArray();
}
